import { useState, useCallback, useMemo, useEffect } from 'react'
import { func, number, string, shape, bool } from 'prop-types'
import {
  button,
  text,
  list,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { Button } from '@groovehq/internal-design-system/lib/components'
import { styles } from './styles'
import AdditionalInfo from './AdditionalInfo'
import PriceDiscount from '../PriceDiscount'
import AddShipping from '../AddShipping'
import SetTax from '../SetTax'

const OrderSummary = ({
  className,
  orderItemsCount,
  currency,
  rawTotal,
  onCloseCreateOrder,
  onSendInvoice,
  invoice,
  btnDisabled,
}) => {
  const [discount, setDiscount] = useState({})
  const [hasTaxes, setHasTaxes] = useState(true)
  const standardFee = 1999 // cents
  const [shipping, setShipping] = useState({
    type: 'free',
  })

  const handleDiscountChange = useCallback(
    change => {
      setDiscount({ ...discount, ...change })
    },
    [discount]
  )

  const handleShippingChange = useCallback(change => {
    setShipping(change)
  }, [])

  const convertFromCents = str => str
  const getDiscountedPrice = useCallback(({ price }) => price * discount, [
    discount,
  ])

  // Subtotal: rawTotal - discount
  const subtotal = useMemo(
    () => {
      let discounted = getDiscountedPrice({
        price: rawTotal,
        discount,
      })
      if (discounted === false) {
        discounted = rawTotal
      }
      return discounted
    },
    [discount, rawTotal, getDiscountedPrice]
  )

  const taxes = useMemo(
    () => {
      // fake tax rate
      const rate = 5
      const taxesCents = hasTaxes ? subtotal * rate / 100 : 0
      return taxesCents
    },
    [hasTaxes, subtotal]
  )

  const refundTotal = useMemo(
    () => {
      return subtotal + taxes + (shipping.fee || 0)
    },
    [shipping.fee, subtotal, taxes]
  )

  const handleSendInvoice = useCallback(
    () => {
      onSendInvoice()
      onCloseCreateOrder()
    },
    [onCloseCreateOrder, onSendInvoice]
  )

  // Remove order discount if there is no items
  useEffect(
    () => {
      if (!orderItemsCount) {
        setDiscount({})
      }
    },
    [orderItemsCount]
  )

  return (
    <>
      <div
        className={['grui flex justify-between', className]}
        css={styles.row}
      >
        <AdditionalInfo className="grui flex-1" />
        <div className="grui text-right mr-12 ml-16">
          <ul
            className="grui pb-12 mb-12"
            css={[list.styles.preflight, styles.summary]}
          >
            <li>
              <PriceDiscount
                currency={currency}
                discount={discount}
                onDiscountChange={handleDiscountChange}
                price={rawTotal}
                isOrder
                disabled={orderItemsCount === 0}
              >
                <button css={button.styles.link} type="button">
                  Add discount
                </button>
              </PriceDiscount>
              <span>
                {discount.number > 0 ? (
                  `−${currency}${convertFromCents(discount.number)}`
                ) : (
                  <>&mdash;</>
                )}
              </span>
            </li>
            <li>
              <span css={text.styles.textMediumDark}>Subtotal</span>
              <span>{`${currency}${convertFromCents(subtotal)}`}</span>
            </li>
            <li>
              <AddShipping
                currency={currency}
                standardFee={standardFee}
                shipping={shipping}
                onShippingChange={handleShippingChange}
              >
                <button css={button.styles.link} type="button">
                  Add shipping
                </button>
              </AddShipping>
              <span>
                {shipping.fee > 0 ? (
                  `${currency}${convertFromCents(shipping.fee)}`
                ) : (
                  <>&mdash;</>
                )}
              </span>
            </li>
            <li>
              <SetTax hasTaxes={hasTaxes} onHasTaxesChange={setHasTaxes}>
                <button css={button.styles.link} type="button">
                  Taxes
                </button>
              </SetTax>
              <span>{`${currency}${convertFromCents(taxes)}`}</span>
            </li>
            <li css={text.styles.fontMedium}>
              <span css={text.styles.textMediumDark}>Total</span>
              <span>{`${currency}${convertFromCents(refundTotal)}`}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="grui text-right mr-12">
        {invoice.sent ? (
          <div className="grui inline-block">
            <p css={[paragraph.styles.preflight, text.styles.textMediumDark]}>
              <span css={text.styles.fontMedium}>Invoice sent</span>
              <> on </>
              <time>{invoice.sent}</time>
            </p>
            <button
              type="button"
              css={button.styles.link}
              onClick={handleSendInvoice}
            >
              Resend invoice
            </button>
          </div>
        ) : (
          <Button
            size="small"
            disabled={btnDisabled}
            onClick={handleSendInvoice}
          >
            Send invoice
          </Button>
        )}
      </div>
    </>
  )
}

OrderSummary.propTypes = {
  orderItemsCount: number,
  currency: string,
  rawTotal: number,
  onCloseCreateOrder: func,
  onSendInvoice: func,
  invoice: shape({
    to: shape({ email: string }),
    from: shape({ email: string, name: string }),
    subject: string,
  }),
  btnDisabled: bool,
  className: string,
}

OrderSummary.defaultProps = {
  orderItemsCount: 0,
  currency: '',
  rawTotal: 0,
  onCloseCreateOrder() {},
  onSendInvoice() {},
  invoice: {},
  btnDisabled: false,
  className: undefined,
}

export default OrderSummary
